import { GetQuickFilterTextParams, ValueGetterParams } from 'ag-grid-community';
import { t } from 'i18next';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { getAddressToDisplay } from 'core/utils';

import { AssociationStatusCell } from './AssociationStatusCell';
import { AssociationTypeCell } from './AssociationTypeCell';
import { PairedProjectsCell } from './PairedProjectsCell';
import { PairedProjectValuesCell } from './PairedProjectValuesCell';
import {
  getAssociationStatus,
  getAssociationStatusLabel,
  getPairedProjectValues,
  IExternalCustomerRow,
  joinValues,
  TPairedProjectField,
} from './utils';

// Keeps pairs searchable when the user hides the Project Numbers / Names columns.
const getQuickFilterTextWithPairs =
  (field: TPairedProjectField) =>
  ({ data, value }: GetQuickFilterTextParams<IExternalCustomerRow>) =>
    joinValues([value, ...getPairedProjectValues(data, field)].filter(Boolean));

const getPairedProjectColumn = (colId: string, headerKey: string, field: TPairedProjectField) => ({
  colId,
  headerName: t(headerKey),
  filter: 'agTextColumnFilter',
  enableRowGroup: false,
  sortable: false,
  valueGetter: ({ data }: ValueGetterParams<IExternalCustomerRow>) =>
    data && getPairedProjectValues(data, field),
  // The quick filter reads this too, so no getQuickFilterText: it would receive the joined string.
  filterValueGetter: ({ data }: ValueGetterParams<IExternalCustomerRow>) =>
    data && joinValues(getPairedProjectValues(data, field)),
  cellRenderer: PairedProjectValuesCell,
  flex: 2,
});

// The paired project columns are added on Programs and left out (not hidden) on other projects, so
// a layout saved on a Program page cannot bring them back elsewhere.
export const columnDef = (isProgram: boolean) => [
  {
    colId: 'accountNumber',
    headerName: t('headerAccountNumber'),
    field: 'accountNumber',
    filter: 'agTextColumnFilter',
    enableRowGroup: false,
    getQuickFilterText: getQuickFilterTextWithPairs('projectNumber'),
    cellRenderer: PairedProjectsCell,
    cellRendererParams: {
      pairedProjectField: 'projectNumber',
    },
    flex: 1,
  },
  {
    colId: 'customerName',
    headerName: t('headerCustomerName'),
    field: 'customerName',
    filter: 'agTextColumnFilter',
    getQuickFilterText: getQuickFilterTextWithPairs('projectName'),
    cellRenderer: PairedProjectsCell,
    cellRendererParams: {
      pairedProjectField: 'projectName',
    },
    flex: 2,
  },
  {
    colId: 'address',
    headerName: t('headerAddress'),
    field: 'address',
    filter: 'agTextColumnFilter',
    enableRowGroup: false,
    sortable: false,
    valueGetter: ({ data }: ValueGetterParams<IExternalCustomerRow>) =>
      data &&
      getAddressToDisplay(
        {
          line1: data.addressLine1,
          line2: data.addressLine2,
          city: data.city,
          state: data.state,
          zip: data.postalCode,
          country: data.country,
        },
        {
          placeholder: DEFAULT_PLACEHOLDER,
          withAccountsReceivable: false,
        }
      ),
    cellRendererParams: {
      maxRows: 2,
    },
    flex: 2,
  },
  {
    colId: 'associationType',
    headerName: t('headerAssociationTypes'),
    field: 'associationType',
    filter: 'agSetColumnFilter',
    enableRowGroup: false,
    cellRenderer: AssociationTypeCell,
    flex: 2,
  },
  {
    colId: 'associationStatus',
    headerName: t('headerAssociationStatus'),
    valueGetter: ({ data }: ValueGetterParams<IExternalCustomerRow>) =>
      data && getAssociationStatus(data.isLinkActive),
    filter: 'agSetColumnFilter',
    filterValueGetter: ({ data }: ValueGetterParams<IExternalCustomerRow>) =>
      data && getAssociationStatusLabel(getAssociationStatus(data.isLinkActive)),
    getQuickFilterText: ({ data }: GetQuickFilterTextParams<IExternalCustomerRow>) =>
      data && getAssociationStatusLabel(getAssociationStatus(data.isLinkActive)),
    enableRowGroup: false,
    // The row-level muting must not fade the status itself.
    cellClass: 'noDecorationCell',
    cellRenderer: AssociationStatusCell,
    flex: 1,
  },
  ...(isProgram
    ? [
        getPairedProjectColumn('projectNumbers', 'headerProjectNumbers', 'projectNumber'),
        getPairedProjectColumn('projectNames', 'headerProjectNames', 'projectName'),
      ]
    : []),
];
